import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sparkle, ArrowsClockwise, Info, Check, X as XIcon, PencilSimple, Lock } from "@phosphor-icons/react";

// Deterministic client-side extractor for "facts" the brand-manager AI is instructed to preserve
// verbatim (dates, dollars, percentages, month+year, numeric quantities, all-caps proper nouns).
// Kept minimal on purpose — no LLM call — so it stays instant on every keystroke.
const MONTHS_RE = /(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(?:uary|ruary|ch|il|ust|tember|ober|ember)?/i;

function extractFacts(text) {
  if (!text) return [];
  const found = new Set();
  const push = (v) => { const t = (v || "").trim(); if (t && t.length <= 40) found.add(t); };

  // Money: $47K, $47,000, $47.5M, $47
  for (const m of text.matchAll(/\$\s?\d{1,3}(?:[,\d]{0,10})?(?:\.\d+)?\s?[KkMmBb]?/g)) push(m[0]);
  // Percentages: 42%, 3.5%
  for (const m of text.matchAll(/\b\d{1,3}(?:\.\d+)?\s?%/g)) push(m[0]);
  // Month + year: "April 2025", "Apr 2024"
  const monthYearRe = new RegExp(MONTHS_RE.source + "\\s+(?:19|20)\\d{2}", "gi");
  for (const m of text.matchAll(monthYearRe)) push(m[0]);
  // Bare 4-digit years 1900-2099 (skipped if already captured above)
  for (const m of text.matchAll(/\b(?:19|20)\d{2}\b/g)) {
    const y = m[0];
    // don't add if already inside a matched Month+Year we captured
    const already = [...found].some((f) => f.includes(y));
    if (!already) push(y);
  }
  // Quarter/Half: Q1 2025, H2 2024
  for (const m of text.matchAll(/\b(?:Q[1-4]|H[12])\s+(?:19|20)\d{2}\b/gi)) push(m[0]);
  // Time counts with units: "3 clients", "12 months", "48 hours", "5 years"
  for (const m of text.matchAll(/\b\d{1,4}(?:,\d{3})*\s+(?:clients?|customers?|users?|subscribers?|followers?|employees?|hires?|leads?|deals?|days?|weeks?|months?|years?|hours?|minutes?|posts?|comments?|dms?|calls?|meetings?|hires?|team[- ]members?|founders?|startups?)\b/gi)) push(m[0]);
  // ALL-CAPS acronyms of 2-6 letters (MRR, ARR, SaaS, YoY, etc.) — but skip if it's a common word
  const COMMON_CAPS = new Set(["I", "OK", "OKAY", "YES", "NO", "USA", "UK", "EU", "US", "AM", "PM"]);
  for (const m of text.matchAll(/\b[A-Z]{2,6}\b/g)) {
    if (!COMMON_CAPS.has(m[0])) push(m[0]);
  }

  // Return in order of first appearance, cap at 8 chips to keep the UI calm.
  const arr = [...found];
  arr.sort((a, b) => text.indexOf(a) - text.indexOf(b));
  return arr.slice(0, 8);
}

const SIGNAL_ORDER = ["hook", "dwell", "save", "comment", "authority", "format"];
const SIGNAL_LABEL = {
  hook: "First-200 hook",
  dwell: "Dwell time",
  save: "Save utility",
  comment: "Comment trigger",
  authority: "Topic authority",
  format: "Format compliance",
};

function scoreColor(s) {
  if (s >= 8) return "#4a7c3f"; // sage
  if (s >= 5) return "var(--accent)";
  return "#9a3324";
}

// Instant deterministic hook/format check for immediate feedback while typing
function localScores(text) {
  const t = (text || "").trim();
  const lines = t.split("\n");
  const nonEmpty = lines.filter((l) => l.trim());
  const firstLine = nonEmpty[0] || "";
  const avgLine = nonEmpty.length ? nonEmpty.reduce((a, l) => a + l.length, 0) / nonEmpty.length : 0;
  const length = t.length;

  // hook
  let hookScore = 0;
  const hookNotes = [];
  if (!length) {
    hookNotes.push("Add a first line - LinkedIn shows only ~210 chars before 'see more'.");
  } else if (firstLine.length > 210) {
    hookScore = 3;
    hookNotes.push(`First line is ${firstLine.length} chars. LinkedIn cuts around 210. Trim.`);
  } else if (firstLine.length > 150) {
    hookScore = 6;
    hookNotes.push(`First line (${firstLine.length} chars) is close to the fold. Tighten for punch.`);
  } else if (firstLine.length < 30) {
    hookScore = 7;
    hookNotes.push("Hook is very short - make sure it earns the click alone.");
  } else {
    hookScore = 9;
  }
  const flow = firstLine.toLowerCase();
  if (hookScore > 0 && /(^| )(i |we |my |our |\$|\d+%| last | yesterday| today)/.test(" " + flow))
    hookScore = Math.min(10, hookScore + 1);

  // format
  let format = 0;
  const fmtNotes = [];
  if (length > 0) {
    if (length <= 3000) format += 3;
    else fmtNotes.push(`Post is ${length} chars. LinkedIn cuts at 3000.`);
    if (avgLine <= 90) format += 4;
    else if (avgLine <= 130) { format += 3; fmtNotes.push("Some lines are long. Break them for scannability."); }
    else { format += 1; fmtNotes.push(`Avg line ${Math.round(avgLine)} chars. Aim <90.`); }
    const shortRatio = nonEmpty.filter((l) => l.length <= 100).length / Math.max(1, nonEmpty.length);
    if (shortRatio >= 0.7) format += 3;
    else if (shortRatio >= 0.5) { format += 2; fmtNotes.push("Add more line breaks."); }
    else { format += 1; fmtNotes.push("Too dense. Break into 1-2 line beats."); }
  }
  return {
    hook: { score: hookScore, notes: hookNotes.length ? hookNotes : ["Strong hook."] },
    format: { score: Math.min(10, format), notes: fmtNotes.length ? fmtNotes : ["Clean formatting."] },
    length,
    first_line_len: firstLine.length,
  };
}

/**
 * GrowthScore
 * Props:
 *   text: string - the draft text
 *   onApplyFix: (fix) => void - called when user clicks "Apply" on a grammar fix
 *   grammarFixes / setGrammarFixes: parent-owned so inline overlay can consume them
 */
export default function GrowthScore({ text, onApplyFix, grammarFixes, setGrammarFixes }) {
  const [aiSignals, setAiSignals] = useState(null); // dwell / save / comment / authority
  const [proposedImprovements, setProposedImprovements] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastScoredText, setLastScoredText] = useState("");
  const [error, setError] = useState("");
  const debounceRef = useRef(null);
  const inFlight = useRef(false);
  // In-memory cache per component instance: text -> { aiSignals, grammarFixes, proposedImprovements }
  const cacheRef = useRef(new Map());

  const local = localScores(text);

  const runAI = async (nextText, force = false) => {
    if (!nextText || nextText.trim().length < 30) {
      setAiSignals(null);
      setProposedImprovements([]);
      if (setGrammarFixes) setGrammarFixes([]);
      return;
    }
    if (!force) {
      if (nextText === lastScoredText) return;
      // Cache hit: skip network call entirely
      const hit = cacheRef.current.get(nextText);
      if (hit) {
        setAiSignals(hit.aiSignals);
        setProposedImprovements(hit.proposedImprovements || []);
        if (setGrammarFixes) setGrammarFixes(hit.grammarFixes || []);
        setLastScoredText(nextText);
        return;
      }
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/draft/score", { text: nextText });
      const s = r.data.signals || {};
      const next = {
        dwell: s.dwell,
        save: s.save,
        comment: s.comment,
        authority: s.authority,
      };
      setAiSignals(next);
      const fixes = r.data.grammar_fixes || [];
      const imps = r.data.proposed_improvements || [];
      if (setGrammarFixes) setGrammarFixes(fixes);
      setProposedImprovements(imps);
      cacheRef.current.set(nextText, { aiSignals: next, grammarFixes: fixes, proposedImprovements: imps });
      // trim cache to last 20 entries
      if (cacheRef.current.size > 20) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }
      setLastScoredText(nextText);
    } catch (e) {
      setError("Scoring paused (rate limit or trial cap). Editable draft still works.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAI(text), 2200);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const signals = {
    hook: local.hook,
    dwell: aiSignals?.dwell,
    save: aiSignals?.save,
    comment: aiSignals?.comment,
    authority: aiSignals?.authority,
    format: local.format,
  };

  const known = SIGNAL_ORDER.map((k) => signals[k]).filter(Boolean);
  const overall = known.length
    ? Math.round((known.reduce((a, s) => a + (s.score || 0), 0) / known.length) * 10) / 10
    : 0;

  const fixes = grammarFixes || [];
  const facts = useMemo(() => extractFacts(text), [text]);
  const dismissFix = (idx) => {
    if (setGrammarFixes) setGrammarFixes(fixes.filter((_, i) => i !== idx));
  };
  const applyFix = (fix, idx) => {
    onApplyFix?.(fix);
    dismissFix(idx);
  };
  const applyAllFixes = () => {
    fixes.forEach((f) => onApplyFix?.(f));
    if (setGrammarFixes) setGrammarFixes([]);
  };

  const dismissImprovement = (idx) => {
    setProposedImprovements((list) => list.filter((_, i) => i !== idx));
  };
  const applyImprovement = (imp, idx) => {
    // `before` empty → append `after` at end (with a blank line for breathing room).
    // Otherwise → replace first occurrence of `before` with `after`.
    if (!imp || !imp.after) return;
    if (imp.before && text.includes(imp.before)) {
      const patched = text.replace(imp.before, imp.after);
      onApplyFix?.({ before: imp.before, after: imp.after, __replaceAll: false, __fullText: patched });
    } else {
      const sep = text.endsWith("\n") ? "\n" : "\n\n";
      onApplyFix?.({ before: "", after: imp.after, __append: true, __fullText: `${text}${sep}${imp.after}` });
    }
    dismissImprovement(idx);
  };
  const applyAllImprovements = () => {
    let patched = text;
    proposedImprovements.forEach((imp) => {
      if (!imp || !imp.after) return;
      if (imp.before && patched.includes(imp.before)) {
        patched = patched.replace(imp.before, imp.after);
      } else {
        const sep = patched.endsWith("\n") ? "\n" : "\n\n";
        patched = `${patched}${sep}${imp.after}`;
      }
    });
    onApplyFix?.({ before: "", after: "", __fullText: patched });
    setProposedImprovements([]);
  };

  const TARGET_LABEL = {
    hook: "Hook",
    dwell: "Dwell",
    save: "Save",
    comment: "Comment",
    authority: "Authority",
    format: "Format",
  };

  return (
    <aside className="card p-5 fade-up" data-testid="growth-score">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <Sparkle size={12} weight="fill" style={{ color: "var(--accent)" }} />
            Growth score
          </div>
          <div className="font-serif text-4xl mt-1" data-testid="growth-overall" style={{ color: scoreColor(overall) }}>
            {overall.toFixed(1)}<span className="text-lg" style={{ color: "var(--ink-3)" }}>/10</span>
          </div>
        </div>
        <button
          onClick={() => runAI(text, true)}
          disabled={busy || !text.trim()}
          className="p-2 rounded-full hover:bg-[color:var(--paper)]"
          data-testid="growth-refresh"
          title="Re-score now"
          aria-label="Re-score"
          style={{color:'var(--ink-3)'}}
        >
          <ArrowsClockwise size={14} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      {facts.length > 0 && (
        <div className="mb-4 rounded-2xl px-3 py-2.5" data-testid="facts-locked" style={{ background: "var(--bg-soft)", border: "1px dashed var(--line-2)" }}>
          <div className="eyebrow mb-1.5 flex items-center gap-1.5" style={{ color: "var(--ink-2)" }}>
            <Lock size={11} weight="fill" style={{ color: "var(--accent)" }} />
            Facts locked · {facts.length}
          </div>
          <div className="flex flex-wrap gap-1">
            {facts.map((f, i) => (
              <span
                key={`${f}-${i}`}
                data-testid={`fact-chip-${i}`}
                className="text-[11px] font-mono px-2 py-0.5 rounded-full"
                style={{ background: "var(--accent-tint)", color: "var(--accent-2)", border: "1px solid var(--accent)" }}
                title="Sacred fact — the AI will preserve this verbatim in every rewrite."
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {SIGNAL_ORDER.map((k) => {
          const s = signals[k];
          const pending = !s && text.trim().length >= 30;
          return (
            <div key={k} data-testid={`growth-${k}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="eyebrow" style={{ color: "var(--ink-2)" }}>{SIGNAL_LABEL[k]}</span>
                <span className="font-mono" style={{ color: s ? scoreColor(s.score) : "var(--ink-3)" }}>
                  {s ? `${s.score}/10` : (pending ? "…" : "—")}
                </span>
              </div>
              <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "var(--line)" }}>
                <div
                  style={{
                    width: `${(s?.score || 0) * 10}%`,
                    background: s ? scoreColor(s.score) : "transparent",
                    height: "100%",
                    transition: "width 400ms ease, background-color 400ms ease",
                  }}
                />
              </div>
              {s?.notes?.[0] && (
                <div className="mt-1.5 text-xs flex gap-1.5" style={{ color: "var(--ink-3)" }}>
                  <Info size={11} className="mt-0.5 shrink-0" />
                  <span>{s.notes[0]}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Proposed growth improvements — atomic edits below the scores */}
      {proposedImprovements.length > 0 && (
        <div className="mt-5 pt-4" style={{borderTop:'1px solid var(--line)'}} data-testid="proposed-improvements-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow flex items-center gap-2" style={{color:'var(--accent-2)'}}>
              <Sparkle size={12} weight="fill" /> Proposed improvements · {proposedImprovements.length}
            </div>
            <button
              onClick={applyAllImprovements}
              data-testid="improvements-apply-all"
              className="text-xs font-medium rounded-full px-3 py-1"
              style={{background:'var(--accent)', color:'#fff'}}
            >Apply all</button>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto scroll-thin pr-1">
            {proposedImprovements.map((imp, i) => {
              const isAppend = !imp.before || !text.includes(imp.before);
              return (
                <div key={i} className="p-3 rounded-2xl" style={{background:'var(--bg-soft)'}} data-testid={`improvement-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-medium">{imp.label}</span>
                    {imp.target && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{background:'var(--accent-tint)', color:'var(--accent-2)'}}>
                        {TARGET_LABEL[imp.target] || imp.target}
                      </span>
                    )}
                    {isAppend && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{background:'var(--bg-elev)', color:'var(--ink-3)'}}>
                        Add to end
                      </span>
                    )}
                  </div>
                  {imp.why && (
                    <div className="text-[11px] mb-2" style={{color:'var(--ink-3)'}}>{imp.why}</div>
                  )}
                  {!isAppend && imp.before && (
                    <div className="text-xs mb-2">
                      <div className="mb-1 text-[10px]" style={{color:'var(--ink-3)'}}>Replace:</div>
                      <div className="whitespace-pre-wrap" style={{background:'#fde5df', color:'#8f2b1a', padding:'6px 10px', borderRadius:12, textDecoration:'line-through'}}>{imp.before}</div>
                    </div>
                  )}
                  <div className="text-xs mb-2">
                    <div className="mb-1 text-[10px]" style={{color:'var(--ink-3)'}}>{isAppend ? "Add this line:" : "With:"}</div>
                    <div className="whitespace-pre-wrap" style={{background:'var(--accent-tint)', color:'var(--accent-2)', padding:'6px 10px', borderRadius:12}}>{imp.after}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => applyImprovement(imp, i)}
                      data-testid={`improvement-apply-${i}`}
                      className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1"
                      style={{background:'var(--accent-tint)', color:'var(--accent-2)', border:'1px solid var(--accent)'}}
                    ><Check size={11}/> Apply</button>
                    <button
                      onClick={() => dismissImprovement(i)}
                      data-testid={`improvement-skip-${i}`}
                      className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1"
                      style={{background:'var(--bg-elev)', color:'var(--ink-3)'}}
                    ><XIcon size={11}/> Skip</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grammar / clarity fixes surfaced automatically alongside scoring */}
      {fixes.length > 0 && (
        <div className="mt-5 pt-4" style={{borderTop:'1px solid var(--line)'}} data-testid="grammar-fixes-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow flex items-center gap-2" style={{color:'var(--accent-2)'}}>
              <PencilSimple size={12} weight="fill" /> Suggested fixes · {fixes.length}
            </div>
            <button
              onClick={applyAllFixes}
              data-testid="grammar-apply-all"
              className="text-xs font-medium rounded-full px-3 py-1"
              style={{background:'var(--accent)', color:'#fff'}}
            >Apply all</button>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto scroll-thin pr-1">
            {fixes.map((f, i) => (
              <div key={i} className="p-3 rounded-2xl" style={{background:'var(--bg-soft)'}} data-testid={`grammar-fix-${i}`}>
                {f.reason && (
                  <div className="text-[11px] mb-1.5" style={{color:'var(--ink-3)'}}>{f.reason}</div>
                )}
                <div className="text-xs mb-2 flex flex-wrap items-center gap-1.5">
                  <span style={{background:'#fde5df', color:'#8f2b1a', textDecoration:'line-through', padding:'2px 6px', borderRadius:8}}>{f.before}</span>
                  <span style={{color:'var(--ink-3)'}}>→</span>
                  <span style={{background:'var(--accent-tint)', color:'var(--accent-2)', padding:'2px 6px', borderRadius:8}}>{f.after}</span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => applyFix(f, i)}
                    data-testid={`grammar-apply-${i}`}
                    className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1"
                    style={{background:'var(--accent-tint)', color:'var(--accent-2)', border:'1px solid var(--accent)'}}
                  ><Check size={11}/> Apply</button>
                  <button
                    onClick={() => dismissFix(i)}
                    data-testid={`grammar-dismiss-${i}`}
                    className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1"
                    style={{background:'var(--bg-elev)', color:'var(--ink-3)'}}
                  ><XIcon size={11}/> Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 pt-4 text-xs font-mono" style={{ color: "var(--ink-3)" }}>
        {local.length} chars · hook {local.first_line_len}c
        {text.trim().length > 0 && text.trim().length < 30 && (
          <div className="mt-1">Write at least 30 chars for AI signals to activate.</div>
        )}
        {error && <div className="mt-1" style={{ color: "var(--accent)" }}>{error}</div>}
      </div>
    </aside>
  );
}
